import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight, HandHeart, User } from "lucide-react";
import { Logo } from "../../components/brand/Logo";
import { PageSkeleton, ThemeToggle } from "../../components/brand/Experience";
import { getExerciseMode, setExerciseMode, useExerciseManifest } from "../../lib/exercises";

export const ModeSwitch = ({ mode, onChange, dark = false }) => (
  <div role="radiogroup" aria-label="Mode d'utilisation" className={`inline-flex rounded-full p-1 ${dark ? "bg-white/10" : "bg-muted"}`}>
    {[["patient", "Patient", User], ["aidant", "Aidant", HandHeart]].map(([k, l, I]) => (
      <button key={k} role="radio" aria-checked={mode === k} data-testid={`exercise-mode-${k}`} onClick={() => onChange(k)}
        className={`flex h-11 items-center gap-2 rounded-full px-4 text-sm font-bold transition-colors ${mode === k ? (k === "aidant" ? "bg-[#E0B33A] text-[#0A2540]" : "bg-brand text-white") : dark ? "text-white/80" : "text-muted-foreground"}`}>
        <I className="h-4 w-4" />{l}
      </button>
    ))}
  </div>
);

export const ExerciseCatalogBody = () => {
  const { data: m } = useExerciseManifest();
  const [mode, setMode] = useState(getExerciseMode);
  const change = (k) => { setExerciseMode(k); setMode(k); };
  if (!m) return <PageSkeleton />;
  return (
    <div data-testid="exercise-catalog">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Exercices guidés</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">{m.disclaimer_fr}</p>
        </div>
        <ModeSwitch mode={mode} onChange={change} />
      </div>
      {m.families.map((f) => (
        <section key={f.id} className="mb-8" data-testid={`family-${f.id}`}>
          <h2 className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary-strong" style={{ fontFamily: "Manrope, sans-serif" }}>{f.name_fr}</h2>
          {f.pathologies.map((p) => (
            <div key={p.id} className="mb-4" data-testid={`pathology-${p.id}`}>
              <p className="mb-2 font-display text-xl font-semibold">{p.name_fr}</p>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {p.exercises.map((e) => (
                  <Link key={e.id} to={`/exercises/${e.id}?mode=${mode}`} data-testid={`exercise-card-${e.id}`}
                    className="shadow-soft lift flex items-center gap-4 rounded-3xl border border-border/70 bg-card p-3 pr-4">
                    <img decoding="async" src={e.steps[0].svg} alt="" className="h-20 w-20 shrink-0 rounded-2xl" loading="lazy" />
                    <div className="min-w-0 flex-1"><p className="font-bold leading-tight">{e.name_fr}</p><p className="mt-1 text-xs text-muted-foreground">{e.goal_fr} · {e.steps.length} étapes</p></div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </section>
      ))}
    </div>
  );
};

export default function ExerciseCatalog() {
  const nav = useNavigate();
  return (
    <div className="baobab-watermark min-h-[100dvh] bg-background">
      <header className="safe-top bg-navy-depth flex items-center justify-between px-4 pb-3 text-white sm:px-8">
        <div className="flex items-center gap-3">
          <button data-testid="exercises-back" onClick={() => nav("/")} aria-label="Retour à l'accueil" className="flex h-11 w-11 items-center justify-center rounded-full bg-white/10"><ArrowLeft className="h-5 w-5" /></button>
          <Logo size={34} /><span className="text-[11px] font-bold tracking-[0.3em] text-[#4FD1C5]">BOMBACARE CONNECT</span>
        </div>
        <ThemeToggle />
      </header>
      <main className="mx-auto max-w-6xl p-4 sm:p-8"><ExerciseCatalogBody /></main>
    </div>
  );
}
